import type { StockLevelIndicatorProps, StockLevelSize, StockLevelStatus } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveStatus(currentStock: number, reorderLevel: number): StockLevelStatus {
  if (currentStock <= 0) return 'out-of-stock';
  if (currentStock <= reorderLevel) return 'low-stock';
  return 'in-stock';
}

const STATUS_LABEL: Record<StockLevelStatus, string> = {
  'in-stock':     'In Stock',
  'low-stock':    'Low Stock',
  'out-of-stock': 'Out of Stock',
};

// Dot color classes
const STATUS_DOT_COLOR: Record<StockLevelStatus, string> = {
  'in-stock':     'bg-success-500',
  'low-stock':    'bg-warning-500',
  'out-of-stock': 'bg-danger-500',
};

// Text color for dot-label variant
const STATUS_TEXT_COLOR: Record<StockLevelStatus, string> = {
  'in-stock':     'text-success-600 dark:text-success-400',
  'low-stock':    'text-warning-600 dark:text-warning-400',
  'out-of-stock': 'text-danger-600 dark:text-danger-400',
};

// Badge bg + text colors
const STATUS_BADGE_COLOR: Record<StockLevelStatus, string> = {
  'in-stock':     'bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400',
  'low-stock':    'bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400',
  'out-of-stock': 'bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400',
};

// Dot dimensions
const SIZE_DOT: Record<StockLevelSize, string> = {
  sm: 'h-2 w-2',   // 8px
  md: 'h-2.5 w-2.5', // 10px
};

const SIZE_TEXT: Record<StockLevelSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
};

// ─── StockLevelIndicator ──────────────────────────────────────────────────────

export function StockLevelIndicator({
  currentStock,
  reorderLevel = 0,
  variant = 'dot',
  showQuantity = false,
  size = 'sm',
}: StockLevelIndicatorProps) {
  const status = resolveStatus(currentStock, reorderLevel);
  const label = STATUS_LABEL[status];

  // aria-label always describes stock level; quantity appended when showQuantity or dot-label/badge
  const ariaLabel = showQuantity
    ? `${label}, ${currentStock} ${currentStock === 1 ? 'unit' : 'units'}`
    : label;

  // ── dot only ─────────────────────────────────────────────────────────────
  if (variant === 'dot') {
    return (
      <span
        role="status"
        aria-label={ariaLabel}
        className={`inline-block rounded-full shrink-0 ${SIZE_DOT[size]} ${STATUS_DOT_COLOR[status]}`}
      />
    );
  }

  // ── dot-label ─────────────────────────────────────────────────────────────
  if (variant === 'dot-label') {
    return (
      <span
        role="status"
        aria-label={ariaLabel}
        className={`inline-flex items-center gap-1.5 ${STATUS_TEXT_COLOR[status]}`}
      >
        <span
          className={`shrink-0 rounded-full ${SIZE_DOT[size]} ${STATUS_DOT_COLOR[status]}`}
          aria-hidden="true"
        />
        <span className={`font-medium ${SIZE_TEXT[size]}`}>
          {label}
          {showQuantity && (
            <span className="ml-1 font-normal text-neutral-400 dark:text-neutral-500">
              ({currentStock})
            </span>
          )}
        </span>
      </span>
    );
  }

  // ── badge ─────────────────────────────────────────────────────────────────
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${SIZE_TEXT[size]} ${STATUS_BADGE_COLOR[status]}`}
    >
      <span
        className={`shrink-0 rounded-full ${SIZE_DOT[size]} ${STATUS_DOT_COLOR[status]}`}
        aria-hidden="true"
      />
      {label}
      {showQuantity && (
        <span className="font-normal opacity-70">
          ({currentStock})
        </span>
      )}
    </span>
  );
}
